import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Bell } from 'lucide-react';
import { NotificationList, NotificationTheme as ListTheme, TabCounts } from './NotificationList';
import { NotificationData, NotificationType } from './NotificationItem';
import { colorTokens } from '@/common/styles/colorTokens';
import { useOnClickOutside } from './useOnClickOutside';
import { OPEN_NOTIFICATIONS_EVENT, OpenNotificationsDetail } from './openNotifications';

export type NotificationTheme = ListTheme;

export interface NotificationIconProps {
  /** 현재 탭의 알림 목록 */
  notifications: NotificationData[];
  /** 각 탭별 미읽음 개수 */
  tabCounts: TabCounts;
  /** 전체 미읽음 개수 (헤더 배지용) */
  totalUnreadCount: number;
  /** 현재 선택된 탭 */
  currentTab: NotificationType;
  /** 로딩 상태 */
  isLoading: boolean;
  /** 더 불러올 데이터가 있는지 */
  hasMore: boolean;
  /** 검색 키워드 */
  searchKeyword: string;
  /** 테마 (startup: 초록, investor: 파랑) */
  theme?: NotificationTheme;
  /** 모두 읽음 처리 콜백 */
  onReadAll: () => void;
  /** 개별 알림 읽음 처리 콜백 */
  onRead: (notification: NotificationData) => void;
  /** 탭 변경 핸들러 */
  onTabChange: (type: NotificationType) => void;
  /** 더 불러오기 콜백 */
  onLoadMore: () => void;
  /** 패널 열림/닫힘 상태 변경 콜백 */
  onOpenChange?: (isOpen: boolean) => void;
  /** 검색어 변경 핸들러 */
  onSearchChange: (keyword: string) => void;
}

export const NotificationIcon: React.FC<NotificationIconProps> = ({
  notifications,
  tabCounts,
  totalUnreadCount,
  currentTab,
  isLoading,
  hasMore,
  searchKeyword,
  theme = 'startup',
  onReadAll,
  onRead,
  onTabChange,
  onLoadMore,
  onOpenChange,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 열림/닫힘 상태 변경 핸들러
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  useOnClickOutside(ref, () => handleOpenChange(false));

  // 홈 등 외부에서 알림 패널 열기 요청
  useEffect(() => {
    const handleOpenRequest = (event: Event) => {
      const detail = (event as CustomEvent<OpenNotificationsDetail>).detail;
      const tab = detail?.tab ?? NotificationType.TASK;
      onTabChange(tab);
      setIsOpen(true);
      onOpenChange?.(true);
    };

    window.addEventListener(OPEN_NOTIFICATIONS_EVENT, handleOpenRequest);
    return () => window.removeEventListener(OPEN_NOTIFICATIONS_EVENT, handleOpenRequest);
  }, [onTabChange, onOpenChange]);

  return (
    <Container ref={ref}>
      <IconButton 
        onClick={() => handleOpenChange(!isOpen)} 
        $isOpen={isOpen}
        $hasUnread={totalUnreadCount > 0}
      >
        <BellIcon $isOpen={isOpen} $hasUnread={totalUnreadCount > 0}>
          <Bell size={20} strokeWidth={2} />
        </BellIcon>
        {totalUnreadCount > 0 && (
          <NotificationBadge $count={totalUnreadCount}>
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </NotificationBadge>
        )}
      </IconButton>
      {isOpen && (
        <NotificationList 
          notifications={notifications}
          tabCounts={tabCounts}
          currentTab={currentTab}
          isLoading={isLoading}
          hasMore={hasMore}
          searchKeyword={searchKeyword}
          onReadAll={onReadAll} 
          onRead={onRead}
          onTabChange={onTabChange}
          onLoadMore={onLoadMore}
          onSearchChange={onSearchChange}
          theme={theme}
        />
      )}
    </Container>
  );
};

const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  10%, 30%, 50%, 70%, 90% { transform: rotate(-8deg); }
  20%, 40%, 60%, 80% { transform: rotate(8deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
`;

const Container = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const IconButton = styled.button<{ $isOpen: boolean; $hasUnread: boolean }>`
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${props => props.$isOpen 
    ? colorTokens.gray[100] 
    : 'transparent'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colorTokens.gray[100]};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    box-shadow: 0 0 0 2px ${colorTokens.blue[500]};
  }
`;

const BellIcon = styled.div<{ $isOpen: boolean; $hasUnread: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$isOpen 
    ? colorTokens.gray[900] 
    : props.$hasUnread 
      ? colorTokens.gray[800]
      : colorTokens.gray[500]};
  transition: color 0.2s ease;
  
  ${props => props.$hasUnread && css`
    animation: ${shake} 0.5s ease-in-out;
    animation-delay: 1s;
    animation-iteration-count: 1;
  `}
  
  ${IconButton}:hover & {
    color: ${colorTokens.gray[900]};
  }
`;

const NotificationBadge = styled.div<{ $count: number }>`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: ${props => props.$count > 9 ? '20px' : '18px'};
  height: 18px;
  padding: 0 ${props => props.$count > 9 ? '5px' : '0'};
  background: linear-gradient(135deg, ${colorTokens.red[500]} 0%, ${colorTokens.red[600]} 100%);
  border-radius: 9px;
  font-size: 0.625rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px ${colorTokens.red[500]}50;
  animation: ${pulse} 2s ease-in-out infinite;
  border: 2px solid white;
`;

export default NotificationIcon;
